// Setup Tour — Part 2 feature-reveal stops + Part 3 finale config.
//
// STEP 0 VERIFY (done): anchors were reconciled against the REAL current
// nav. The primary shell nav is the Cockpit LeftSidebar (intent ids like
// `command`, `leads`, `phone`, …) mapped to concrete tabs via
// COCKPIT_ROUTES. Each sidebar button is tagged `data-tour-id="nav-<id>"`
// (single edit in LeftSidebar.jsx), so a stop that maps to a sidebar
// intent gets a real highlight. Three surfaces are NOT in the sidebar and
// are reached by concrete tab id instead:
//   · Workflows & Drips → tab `drips` (subnav button carries data-tab-id="drips")
//   · Health Command Center → tab `health_command_center` (top-nav "Health CC")
//   · Scorecard & Money Map → tab `scorecard` (top-nav button tagged data-tour-id="nav-scorecard")
// The rumored "Twilio → Relay" rename is label-only; the dialer surface is
// still intent `phone` / tab `dial`, so stop 3 anchors on `nav-phone`.
//
// Each stop deep-links: navigating is what makes the operator see the real
// screen behind the spotlight. `intent` (when set) routes through the same
// intent→tab mapping the sidebar uses so it works in every workspace mode;
// otherwise `tab` is passed straight to setTab. `anchors` is an ordered
// list of CSS selectors — the Spotlight highlights the first that resolves,
// and falls back to a centered card when none do.

'use strict';

export const REVEAL_STOPS = [
  {
    key: 'command_center',
    title: 'Command Center',
    intent: 'command',
    tab: 'home',
    anchors: ['[data-tour-id="nav-command"]'],
    copy: 'Your day starts here — your numbers, what needs attention, and your next best action, every morning.',
  },
  {
    key: 'leads_pipeline',
    title: 'Leads & Pipeline',
    intent: 'leads',
    tab: 'leads',
    anchors: ['[data-tour-id="nav-leads"]'],
    copy: 'Every lead lives here, scored by the AI so you always know who to call first.',
  },
  {
    key: 'dialer',
    title: 'The Call Button',
    intent: 'phone',
    tab: 'dial',
    anchors: ['[data-tour-id="nav-phone"]'],
    copy: 'Call from anywhere you see a name — pre-call briefing before, automatic notes and follow-up after.',
  },
  {
    key: 'conversations',
    title: 'Conversations',
    intent: 'conversations',
    tab: 'inbox',
    anchors: ['[data-tour-id="nav-conversations"]'],
    copy: 'Every text with every client, in one inbox — including the ones your AI sends for you.',
  },
  {
    key: 'workflows_drips',
    title: 'Workflows & Drips',
    intent: null,
    tab: 'drips',
    anchors: ['[data-tab-id="drips"]'],
    copy: 'This is the automation room: welcome texts, renewal reminders, birthday emails — set once, runs forever.',
  },
  {
    key: 'ai_team',
    title: 'AI Team',
    intent: 'ai_team',
    tab: 'ai_staff',
    anchors: ['[data-tour-id="nav-ai_team"]'],
    copy: 'Meet your AI employees — they follow up, watch renewals, and work your book around the clock. You decide how much autonomy they get.',
  },
  {
    key: 'your_book',
    title: 'Your Book',
    intent: 'clients',
    tab: 'clients',
    anchors: ['[data-tour-id="nav-clients"]'],
    copy: 'Your whole book, always current — every policy, every renewal date, every at-risk client flagged.',
  },
  {
    key: 'health_command_center',
    title: 'Health Command Center',
    intent: null,
    tab: 'health_command_center',
    anchors: ['[data-tour-id="nav-health_command_center"]'],
    copy: 'The health-business cockpit: book health score, revenue at risk, hot opportunities — the big picture in one screen.',
  },
  {
    key: 'scorecard_moneymap',
    title: 'Scorecard & Money Map',
    intent: null,
    tab: 'scorecard',
    anchors: ['[data-tour-id="nav-scorecard"]'],
    copy: "How you're really doing: commissions, persistency, chargebacks — the numbers that decide your income.",
  },
  {
    key: 'tasks_calendar',
    title: 'Tasks & Calendar',
    intent: 'calendar',
    tab: 'calendar',
    anchors: ['[data-tour-id="nav-calendar"]'],
    copy: 'Everything the AI schedules or you promise a client lands here — nothing falls through the cracks.',
  },
];

// Part 3 — the AI finale. Highlights the floating AI launcher (ChatPanel's
// purple "✨ AI" pill, tagged data-tour-id="ai-launcher"). The last screen
// is NOT a Finish button — it's the inline "ask your AI" input below.
export const FINALE = Object.freeze({
  key: 'ai_finale',
  title: 'Your AI knows this app',
  anchors: ['[data-tour-id="ai-launcher"]'],
  copy: "Whenever you're not sure where something is or how to do it — just ask. The AI knows this app better than any menu.",
  inputLabel: 'Try it now — ask your AI one thing about your book',
  inputPlaceholder: 'e.g. Which of my clients have a renewal coming up?',
});

export const ECHO_COPY = 'Stuck? Ask me.';
